import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Save, Copy, BadgeCheck, ShieldQuestion, ListChecks } from "lucide-react";

const ASSOCS = ["PRCA", "CCA", "CPRA", "APRA"];
const YEARS = [2025, 2026, 2027, 2028];
const RULE_TYPES = [
  { v: "max_official", l: "Maximum Official" },
  { v: "min_required", l: "Minimum Required" },
  { v: "no_count", l: "No Count Requirement" },
];

export default function CountRulesEditor() {
  const [assoc, setAssoc] = useState("PRCA");
  const [season, setSeason] = useState(2026);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/count-rules?association=${assoc}&season=${season}`).then((r) => setDoc(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assoc, season]);

  const setEv = (i, key, val) => setDoc((d) => {
    const events = [...d.events];
    events[i] = { ...events[i], [key]: val };
    return { ...d, events };
  });
  const addRule = () => setDoc((d) => ({ ...d, configured: true, association: assoc, season,
    events: [...(d.events || []), { event: "New Event", rule_type: "max_official", max_official: 80, min_required: null, official_unofficial: true, recommended_added_money: 2000, tr_roles: null, special_rule: "", status: "active" }] }));

  const save = async (verified) => {
    setSaving(true);
    try {
      await api.put("/admin/count-rules", { association: assoc, season, events: doc.events || [],
        source: doc.source || "", notes: doc.notes || "", verified: verified ?? doc.verified });
      toast.success("Rules saved — live everywhere immediately");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const copyPrev = async () => {
    try {
      await api.post("/admin/count-rules/copy", { association: assoc, from_season: season - 1, to_season: season });
      toast.success(`Copied ${assoc} ${season - 1} → ${season} (needs verification)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "No previous season to copy"); }
  };

  return (
    <div className="rounded-xl border-2 border-[#8C6239]/25 bg-[#FBF7F2] p-5 mb-6" data-testid="count-rules-editor">
      <div className="font-display font-bold mb-1 flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#8C6239]" /> Rodeo Count Rules</div>
      <p className="text-sm text-slate-600 mb-4">The single source of truth for every rodeo-count calculation. Editable per association &amp; season — no redeploy needed.</p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={assoc} onChange={(e) => setAssoc(e.target.value)} data-testid="cr-assoc" className="text-sm rounded-lg border border-slate-300 px-3 py-2 bg-white">
          {ASSOCS.map((a) => <option key={a}>{a}</option>)}
        </select>
        <select value={season} onChange={(e) => setSeason(Number(e.target.value))} data-testid="cr-season" className="text-sm rounded-lg border border-slate-300 px-3 py-2 bg-white">
          {YEARS.map((y) => <option key={y}>{y}</option>)}
        </select>
        {doc?.configured && (doc.verified
          ? <span className="text-xs font-bold text-[#3B8A2B] flex items-center gap-1"><BadgeCheck className="w-4 h-4" /> Verified{doc.verified_date ? ` · ${new Date(doc.verified_date).toLocaleDateString()}` : ""}</span>
          : <span className="text-xs font-bold text-[#B8860B] flex items-center gap-1"><ShieldQuestion className="w-4 h-4" /> Needs verification</span>)}
      </div>

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#8C6239]" /></div>
        : !doc?.configured ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center">
            <p className="text-sm text-slate-600 mb-3">{assoc} {season} rodeo-count rules have not yet been configured.</p>
            <div className="flex justify-center gap-2">
              <button onClick={copyPrev} data-testid="cr-copy" className="inline-flex items-center gap-2 text-sm font-semibold bg-slate-800 text-white rounded-lg px-4 py-2"><Copy className="w-4 h-4" /> Copy {season - 1}</button>
              <button onClick={addRule} className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 rounded-lg px-4 py-2"><Plus className="w-4 h-4" /> Add rule</button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Event</th>
                    <th className="text-left px-3 py-2">Rule Type</th>
                    <th className="px-2 py-2">Max Official</th>
                    <th className="px-2 py-2">Min Required</th>
                    <th className="px-2 py-2">Off/Unoff</th>
                    <th className="px-2 py-2">Rec. $ Added</th>
                    <th className="px-2 py-2">TR Roles</th>
                  </tr>
                </thead>
                <tbody>
                  {(doc.events || []).map((e, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`cr-row-${i}`}>
                      <td className="px-2 py-1.5"><input value={e.event} onChange={(ev) => setEv(i, "event", ev.target.value)} className="w-40 border border-slate-200 rounded px-2 py-1" /></td>
                      <td className="px-2 py-1.5">
                        <select value={e.rule_type} onChange={(ev) => setEv(i, "rule_type", ev.target.value)} className="border border-slate-200 rounded px-1 py-1">
                          {RULE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5"><input type="number" value={e.max_official ?? ""} onChange={(ev) => setEv(i, "max_official", ev.target.value === "" ? null : Number(ev.target.value))} className="w-20 border border-slate-200 rounded px-2 py-1" data-testid={`cr-max-${i}`} /></td>
                      <td className="px-2 py-1.5"><input type="number" value={e.min_required ?? ""} onChange={(ev) => setEv(i, "min_required", ev.target.value === "" ? null : Number(ev.target.value))} className="w-20 border border-slate-200 rounded px-2 py-1" data-testid={`cr-min-${i}`} /></td>
                      <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!e.official_unofficial} onChange={(ev) => setEv(i, "official_unofficial", ev.target.checked)} /></td>
                      <td className="px-2 py-1.5"><input type="number" value={e.recommended_added_money ?? ""} onChange={(ev) => setEv(i, "recommended_added_money", ev.target.value === "" ? null : Number(ev.target.value))} className="w-24 border border-slate-200 rounded px-2 py-1" /></td>
                      <td className="px-2 py-1.5">
                        <select value={e.tr_roles || ""} onChange={(ev) => setEv(i, "tr_roles", ev.target.value || null)} className="border border-slate-200 rounded px-1 py-1">
                          <option value="">—</option><option value="combined">Combined</option><option value="separate">Separate</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input value={doc.source || ""} onChange={(e) => setDoc((d) => ({ ...d, source: e.target.value }))} placeholder="Source (e.g. 2026 PRCA Rule Book)" className="flex-1 min-w-[220px] text-sm border border-slate-300 rounded-lg px-3 py-2" data-testid="cr-source" />
              <button onClick={addRule} className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2"><Plus className="w-4 h-4" /> Add rule</button>
              <button onClick={copyPrev} className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2"><Copy className="w-4 h-4" /> Copy {season - 1}</button>
              <button onClick={() => save(false)} disabled={saving} data-testid="cr-save" className="inline-flex items-center gap-2 text-sm font-bold bg-[#8C6239] text-white rounded-lg px-4 py-2 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
              <button onClick={() => save(true)} disabled={saving} data-testid="cr-verify" className="inline-flex items-center gap-2 text-sm font-bold bg-[#3B8A2B] text-white rounded-lg px-4 py-2 disabled:opacity-60"><BadgeCheck className="w-4 h-4" /> Save &amp; Mark Verified</button>
            </div>
          </>
        )}
    </div>
  );
}
